// app/javascript/src/containers/Scheduling/EventType.tsx
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { matchPath, Prompt, Redirect, Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import copy from 'copy-to-clipboard'
import styled from 'styled-components'
import TopNavigation from '../../components/Navigation/TopNavigation'
import TopSubnavigation from '../../components/Navigation/TopSubnavigation'
import TabItem from '../../components/Navigation/TabItem'
import ScrollToTopOnMount from '../../components/Effects/ScrollToTopOnMount'
import FullPageContent from '../../components/Page/FullPageContent'
import PageHeader from '../../components/Page/PageHeader'
import PageLoader from '../../components/Page/PageLoader'
import Button from '../../components/Button/Button'
import Badge from '../../components/Badge/Badge'
import Notification from '../../utilities/Notification'
import { Style } from '../../styles'
import { EventTypesController } from '../../controllers'
import { showConfirmModal } from '../../store/modals/actions'
import { ModalsActionTypes } from '../../store/modals/types'
import { AppState } from '../../store'
import { bookingHost, slugify } from '../../components/Scheduling/slug'
import ERoute from '../../ERoute'
import RouteHelper from '../../helpers/RouteHelper'
import { EventType as EventTypeModel } from '../../types'
import GeneralTab from '../../components/Scheduling/tabs/GeneralTab'
import AvailabilityTab from '../../components/Scheduling/tabs/AvailabilityTab'
import LimitsTab from '../../components/Scheduling/tabs/LimitsTab'
import { eventTypeReducer, initialEditorState, isDirty } from './eventTypeReducer'

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`

const Content = styled.div`
  flex: 0 0 600px;
  max-width: 100%;
  padding: ${Style.spacing.x3};
  overflow: auto;
  border-right: 1px solid ${Style.color.border};

  @media screen and (max-width: ${Style.breakpoints.LARGE}) {
    flex: 1;
    border-right: none;
  }

  @media screen and (max-width: ${Style.breakpoints.SMALL}) {
    padding: ${Style.spacing.x2};
  }
`

// The booker renders in an iframe of the public booking page so its styles don't collide with the app's
const Preview = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f6f6f7;

  @media screen and (max-width: ${Style.breakpoints.LARGE}) {
    display: none;
  }
`

const PageLoaderContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`

const HeaderTitle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${Style.spacing.x1};
`


export default function EventType() {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const { id } = useParams<{ id: string }>()
  const storeDispatch = useDispatch() as (action: ModalsActionTypes) => void
  const currentUserId = useSelector<AppState, string | undefined>(s => s.authentication.currentUser?.id)
  const [state, dispatch] = React.useReducer(eventTypeReducer, initialEditorState)
  const [saving, setSaving] = React.useState(false)
  // Bumped whenever the server-side event type changes so the preview iframe reloads
  const [previewVersion, setPreviewVersion] = React.useState(0)
  const leavingRef = React.useRef(false)

  const eventTypePath = RouteHelper.process(ERoute.PATH_SCHEDULING_EVENT_TYPE, { id })

  React.useEffect(() => {
    let cancelled = false
    EventTypesController.getEventType(id).then(eventType => {
      if (cancelled) return
      if (eventType?.id) dispatch({ type: 'loaded', eventType })
      else { Notification.notifyError(t('Scheduling::Event type not found')); history.replace(ERoute.PATH_SCHEDULING_EVENT_TYPES) }
    }).catch(() => { if (!cancelled) { Notification.notifyError(t('Scheduling::Could not load event type')); history.replace(ERoute.PATH_SCHEDULING_EVENT_TYPES) } })
    return () => { cancelled = true }
  }, [id])

  const dirty = isDirty(state)

  React.useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => { if (dirty) { e.preventDefault(); e.returnValue = '' } }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [dirty])

  const draft = state.draft
  const onChange = (patch: Partial<EventTypeModel>) => dispatch({ type: 'change', patch })

  const isHost = !!draft && !!currentUserId && draft.host?.id === currentUserId

  const save = async () => {
    if (!draft || saving) return
    setSaving(true)
    try {
      const slug = slugify(draft.slug)
      const hostSlug = slugify(state.hostBookingSlug)
      const hostSlugChanged = isHost && (state.saved?.booking_slug || '') !== hostSlug
      const res = await EventTypesController.update({ ...draft, slug, id: draft.id!, ...(hostSlugChanged ? { host_booking_slug: hostSlug } : {}) })
      if (res.id) { dispatch({ type: 'saved', eventType: res }); setPreviewVersion(v => v + 1); Notification.notifySuccess(t('Scheduling::Event type saved')) }
      else {
        const first = res.errors?.[0]
        Notification.notifyError(first ? `${first.field ? `${first.field} ` : ''}${first.message}` : t('Scheduling::Could not save'))
      }
    } catch { Notification.notifyError(t('Scheduling::Could not save')) }
    setSaving(false)
  }

  const remove = () => {
    if (!draft) return
    storeDispatch(showConfirmModal({
      title: t('Scheduling::Delete event type'),
      description: t('Scheduling::Delete "{{title}}"?', { title: draft.title }),
      action: { label: t('Scheduling::Delete'), isDestructive: true },
      onConfirm: async () => {
        try {
          const res = await EventTypesController.delete(draft.id!)
          if (res.error) Notification.notifyError(t('Scheduling::Could not delete event type'))
          else { leavingRef.current = true; history.push(ERoute.PATH_SCHEDULING_EVENT_TYPES) }
        } catch { Notification.notifyError(t('Scheduling::Could not delete event type')) }
      },
    }))
  }

  const copyLink = () => {
    if (!draft?.public_url) return
    copy(draft.public_url)
    Notification.notifySuccess(t('Scheduling::Link copied'))
  }

  const preview = () => {
    if (draft?.public_url) window.open(draft.public_url, '_blank', 'noopener')
  }

  // Saved right away (like the list toggle) so it can't be left as an unsaved draft change while the link stays bookable
  const toggleActive = async () => {
    if (!draft) return
    const active = !draft.active
    try {
      const res = await EventTypesController.update({ id: draft.id!, active })
      if (res.id) { dispatch({ type: 'activeSaved', active: res.active }); setPreviewVersion(v => v + 1) }
      else Notification.notifyError(t('Scheduling::Could not save'))
    } catch { Notification.notifyError(t('Scheduling::Could not save')) }
  }

  const onMainActionClick = (key: string) => {
    switch (key) {
      case 'preview': preview()
        break
      case 'copy': copyLink()
        break
      case 'toggle-active': toggleActive()
        break
      case 'delete': remove()
        break
    }
  }

  const tabs: Array<{ path: ERoute, label: string }> = [
    { path: ERoute.PATH_SCHEDULING_EVENT_TYPE_GENERAL, label: t('Scheduling::General') },
    { path: ERoute.PATH_SCHEDULING_EVENT_TYPE_AVAILABILITY, label: t('Scheduling::Availability') },
    { path: ERoute.PATH_SCHEDULING_EVENT_TYPE_LIMITS, label: t('Scheduling::Limits & buffers') },
  ]

  return (
    <>
      <Helmet>
        <title>{t('Scheduling::{{__appName}} | Event type')}</title>
      </Helmet>
      <ScrollToTopOnMount />
      <Prompt when={dirty} message={(next) => leavingRef.current || next.pathname.startsWith(`${eventTypePath}/`) ? true : t('Scheduling::You have unsaved changes. Leave anyway?')} />

      <TopNavigation
        icon='calendar-clock'
        title={t('Scheduling::Event type')}
        action={draft ? (
          <Button
            type='success'
            text={t('Scheduling::Save')}
            onClick={save}
            disabled={!dirty}
            isLoading={saving}
            extraClasses='page-action'
          />
        ) : undefined}
      />

      <FullPageContent>
        {!draft && <PageLoaderContainer><PageLoader /></PageLoaderContainer>}

        {draft && <>
          <TopSubnavigation>
            {tabs.map(item => (
              <TabItem
                key={item.path}
                active={!!matchPath(location.pathname, { path: item.path })}
                onClick={() => history.push(RouteHelper.process(item.path, { id }))}
              >
                {item.label}
              </TabItem>
            ))}
          </TopSubnavigation>

          <Container>
            <Content>
              <PageHeader
                breadcrumbs={[
                  { content: t('Scheduling::Back'), url: 'javascript://', onClick: () => history.push(ERoute.PATH_SCHEDULING_EVENT_TYPES) },
                ]}
                title={
                  <HeaderTitle>
                    {draft.title || t('Scheduling::Untitled')}
                    {!draft.active && <Badge type='grey' text={t('Scheduling::Inactive')} />}
                  </HeaderTitle>
                }
                mainActions={[
                  { key: 'preview', icon: 'external-link', content: t('Scheduling::Preview'), visible: !!draft.public_url },
                  { key: 'copy', icon: 'copy', content: t('Scheduling::Copy link'), visible: !!draft.public_url },
                  { key: 'toggle-active', icon: draft.active ? 'eye-closed' : 'eye', content: draft.active ? t('Scheduling::Deactivate') : t('Scheduling::Activate') },
                  { key: 'delete', icon: 'trash-alt-solid', content: t('Scheduling::Delete'), destructive: true },
                ]}
                onMainActionClick={onMainActionClick}
              />

              <Switch>
                <Route path={ERoute.PATH_SCHEDULING_EVENT_TYPE_GENERAL}>
                  <GeneralTab draft={draft} onChange={onChange} hostBookingSlug={state.hostBookingSlug}
                    onHostBookingSlugChange={(value) => dispatch({ type: 'hostSlug', value })} bookingHost={bookingHost()} canEditHostSlug={isHost} />
                </Route>
                <Route path={ERoute.PATH_SCHEDULING_EVENT_TYPE_AVAILABILITY}>
                  <AvailabilityTab draft={draft} onChange={onChange} onScheduleUpdated={() => EventTypesController.getEventType(id).then(et => { if (et.id) { dispatch({ type: 'scheduleRefreshed', weekSchedule: et.week_schedule || [] }); setPreviewVersion(v => v + 1) } }).catch(() => Notification.notifyError(t('Scheduling::Could not refresh availability')))} />
                </Route>
                <Route path={ERoute.PATH_SCHEDULING_EVENT_TYPE_LIMITS}>
                  <LimitsTab draft={draft} onChange={onChange} />
                </Route>
                <Redirect to={RouteHelper.process(ERoute.PATH_SCHEDULING_EVENT_TYPE_GENERAL, { id })} />
              </Switch>
            </Content>

            <Preview>
              {state.saved?.preview_url
                ? <iframe key={previewVersion} src={state.saved.preview_url} title={t('Scheduling::Preview')} className='size-full border-0' />
                : <p className='text-muted-foreground p-6 text-center'>{t('Scheduling::Set a booking link to preview this event type')}</p>}
            </Preview>
          </Container>
        </>}
      </FullPageContent>
    </>
  )
}
